import React, { useEffect, useState } from 'react';
import { sows as sowsApi, estimates as estApi } from '@/utils/api';
import { Plus, FileSignature, X, Download, Share2, Sparkles, Check, Trash2, Edit2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  Draft: { bg: '#E8E3D9', color: '#5C6B72' },
  Sent: { bg: '#E6F1F4', color: '#006080' },
  Signed: { bg: '#3C8A5A15', color: '#3C8A5A' },
};

function SignModal({ sow, projectId, onClose, onSigned }) {
  const [name, setName] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) return toast.error('Enter your full legal name');
    if (!agree) return toast.error('You must agree');
    setBusy(true);
    try {
      const r = await sowsApi.sign(projectId, sow.id, { signer_name: name.trim(), agree: true });
      onSigned(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Sign failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="card-base w-full max-w-lg p-5 animate-fade-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold" style={{ color: 'var(--ink)' }}>Sign — {sow.title}</h3>
          <button onClick={onClose} style={{ color: 'var(--muted)' }}><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <p className="text-xs" style={{ color: 'var(--muted)' }}>By signing electronically you agree to the terms in this Statement of Work. Your name, timestamp, IP address, and browser will be recorded.</p>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Type your full legal name</label>
            <input className="input-agency text-sm" value={name} onChange={e => setName(e.target.value)} placeholder="John Carter" data-testid="sign-name-input" />
          </div>
          <label className="flex items-start gap-2 text-xs" style={{ color: 'var(--ink)' }}>
            <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} data-testid="sign-agree" />
            <span>I agree to the terms of this Statement of Work and consent to electronic signing.</span>
          </label>
          <div className="flex gap-3 pt-2">
            <button type="button" className="flex-1 py-2 text-sm rounded-btn border font-medium" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={onClose}>Cancel</button>
            <button type="button" className="flex-1 btn-primary py-2 text-sm flex items-center justify-center gap-1.5" disabled={busy || !name.trim() || !agree} onClick={submit} data-testid="sign-submit">
              <Check size={14} /> {busy ? 'Signing…' : 'Sign SOW'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditorModal({ initial, estimates, onClose, onSave, projectId, isNew }) {
  const [draft, setDraft] = useState(initial || { title: '', content: '', estimate_id: '' });
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);

  const aiDraft = async () => {
    if (!draft.estimate_id) return toast.error('Pick an estimate to draft from');
    setDrafting(true);
    try {
      const r = await sowsApi.draft(projectId, draft.estimate_id);
      setDraft(d => ({ ...d, title: d.title || r.data.title, content: r.data.content }));
      toast.success('AI draft generated');
    } catch (e) { toast.error(e.response?.data?.detail || 'AI draft failed (Claude key?)'); }
    finally { setDrafting(false); }
  };

  const save = async () => {
    if (!draft.title || !draft.content) return toast.error('Title + content required');
    setBusy(true);
    try {
      await onSave(draft);
      onClose();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="card-base w-full max-w-3xl p-5 animate-fade-in overflow-y-auto max-h-[90vh]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold" style={{ color: 'var(--ink)' }}>{isNew ? 'New SOW' : 'Edit SOW'}</h3>
          <button onClick={onClose} style={{ color: 'var(--muted)' }}><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Title</label>
              <input className="input-agency text-sm" value={draft.title} onChange={e => setDraft({...draft, title: e.target.value})} data-testid="sow-title" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Link to estimate</label>
              <select className="input-agency text-sm" value={draft.estimate_id || ''} onChange={e => setDraft({...draft, estimate_id: e.target.value})}>
                <option value="">(none)</option>
                {estimates.filter(e => e.status === 'Approved').map(e => (
                  <option key={e.id} value={e.id}>{e.currency} {e.total?.toFixed(0)} — v{e.version}</option>
                ))}
              </select>
            </div>
          </div>
          <button type="button" disabled={!draft.estimate_id || drafting} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-btn font-semibold disabled:opacity-50" style={{ background: '#E6F1F4', color: '#006080' }} onClick={aiDraft} data-testid="ai-draft-sow-button">
            <Sparkles size={12} /> {drafting ? 'Drafting with Claude…' : 'AI Draft from Estimate'}
          </button>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Content (Markdown)</label>
            <textarea className="input-agency text-sm font-mono resize-none" rows={20} value={draft.content} onChange={e => setDraft({...draft, content: e.target.value})} data-testid="sow-content" />
          </div>
          <div className="flex gap-3">
            <button type="button" className="flex-1 py-2 text-sm rounded-btn border font-medium" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={onClose}>Cancel</button>
            <button type="button" className="flex-1 btn-primary py-2 text-sm" disabled={busy} onClick={save} data-testid="sow-save-button">{busy ? 'Saving…' : 'Save SOW'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SowsTab({ projectId, isOperator }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estimates, setEstimates] = useState([]);
  const [editor, setEditor] = useState(null); // {item, isNew}
  const [signing, setSigning] = useState(null);

  const reload = () => sowsApi.list(projectId).then(r => setList(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => {
    reload();
    if (isOperator) estApi.list(projectId).then(r => setEstimates(r.data)).catch(() => {});
  }, [projectId, isOperator]);

  const handleSave = async (draft) => {
    try {
      if (draft.id) {
        await sowsApi.update(projectId, draft.id, { title: draft.title, content: draft.content });
        toast.success('SOW updated');
      } else {
        await sowsApi.create(projectId, { title: draft.title, content: draft.content, estimate_id: draft.estimate_id || null });
        toast.success('SOW created');
      }
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const share = async (id) => {
    await sowsApi.share(projectId, id);
    reload();
    toast.success('SOW shared with client');
  };

  const del = async (id) => {
    if (!window.confirm('Delete this SOW?')) return;
    try {
      await sowsApi.delete(projectId, id);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="animate-fade-in" data-testid="sows-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Statements of Work</h3>
        {isOperator && (
          <button className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5" onClick={() => setEditor({ item: { title: '', content: '' }, isNew: true })} data-testid="new-sow-button">
            <Plus size={13} /> New SOW
          </button>
        )}
      </div>

      {loading ? (
        <div className="h-24 rounded-card animate-pulse" style={{ background: 'var(--border)' }} />
      ) : list.length === 0 ? (
        <div className="card-base p-8 text-center">
          <FileSignature size={32} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>No SOWs yet</p>
          {isOperator && <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Draft a SOW from an approved estimate and send it for e-signing.</p>}
        </div>
      ) : (
        <div className="space-y-3" data-testid="sows-list">
          {list.map(s => {
            const st = STATUS_STYLES[s.status] || STATUS_STYLES.Draft;
            return (
              <div key={s.id} className="card-base p-4" data-testid={`sow-${s.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-bold text-sm truncate" style={{ color: 'var(--ink)' }}>{s.title}</p>
                      <span className="badge-status" style={{ background: st.bg, color: st.color }}>{s.status}</span>
                    </div>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                      Updated {new Date(s.updated_at || s.created_at).toLocaleDateString()}
                      {s.signatures?.length > 0 && (
                        <> · Signed by <b>{s.signatures[s.signatures.length - 1].signer_name}</b></>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ background: 'var(--surface)', color: 'var(--muted)', border: '1px solid var(--border)' }} onClick={() => window.open(sowsApi.pdfUrl(projectId, s.id), '_blank')} data-testid={`sow-pdf-${s.id}`}>
                    <Download size={12} /> PDF
                  </button>
                  {isOperator && s.status === 'Draft' && (
                    <>
                      <button className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ background: '#E6F1F4', color: '#006080' }} onClick={() => setEditor({ item: s, isNew: false })} data-testid={`sow-edit-${s.id}`}>
                        <Edit2 size={12} /> Edit
                      </button>
                      <button className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ background: '#E6F1F4', color: '#006080' }} onClick={() => share(s.id)} data-testid={`sow-share-${s.id}`}>
                        <Share2 size={12} /> Send to Client
                      </button>
                      <button className="text-xs px-3 py-1.5 rounded-btn flex items-center gap-1" style={{ color: '#B34A3A' }} onClick={() => del(s.id)} data-testid={`sow-delete-${s.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </>
                  )}
                  {!isOperator && s.status === 'Sent' && (
                    <button className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ background: '#3C8A5A', color: 'white' }} onClick={() => setSigning(s)} data-testid={`sow-sign-${s.id}`}>
                      <FileSignature size={12} /> Sign & Accept
                    </button>
                  )}
                </div>
                {/* Content preview */}
                <div className="mt-3 p-3 rounded-btn text-xs whitespace-pre-wrap line-clamp-6" style={{ background: 'var(--bg)', color: 'var(--ink)', maxHeight: 160, overflow: 'auto' }}>
                  {s.content?.slice(0, 800)}{s.content?.length > 800 ? '…' : ''}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editor && (
        <EditorModal
          initial={editor.item}
          estimates={estimates}
          isNew={editor.isNew}
          projectId={projectId}
          onClose={() => setEditor(null)}
          onSave={handleSave}
        />
      )}
      {signing && (
        <SignModal sow={signing} projectId={projectId} onClose={() => setSigning(null)} onSigned={() => { setSigning(null); reload(); toast.success('SOW signed'); }} />
      )}
    </div>
  );
}
